import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AppLayout } from '@/widgets/layout/AppLayout';
import { AuthGuard } from './AuthGuard';
import { EmailVerificationGuard } from './EmailVerificationGuard';
import { TenderCabinetGuard } from './TenderCabinetGuard';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import { CookieConsentBanner } from '@/widgets/cookie-consent';

const ProfilePage = lazy(() => import('@/pages/profile/ui/ProfilePage'));
const ProfileEditPage = lazy(() => import('@/pages/profile/ui/ProfileEditPage'));
const CompanyTeamPage = lazy(() => import('@/pages/profile/team/CompanyTeamPage'));
const DashboardPage = lazy(() => import('@/pages/dashboard/ui/DashboardPage'));
const OrganizationsPage = lazy(() => import('@/pages/organizations/ui/OrganizationsPage'));
const OrganizationDetailPage = lazy(
  () => import('@/pages/organizations/ui/OrganizationDetailPage'),
);
const OrganizationInvitePage = lazy(
  () => import('@/pages/organizations/ui/OrganizationInvitePage'),
);
const ActivityPage = lazy(() => import('@/pages/activity/ui/ActivityPage'));
const ActivityHistoryPage = lazy(() => import('@/pages/activity/ui/ActivityHistoryPage'));
const StatisticsPage = lazy(() => import('@/pages/statistics/ui/StatisticsPage'));
const InterfaceSettingsPage = lazy(() => import('@/pages/settings/ui/InterfaceSettingsPage'));
const SecuritySettingsPage = lazy(() => import('@/pages/settings/ui/SecuritySettingsPage'));
const NotificationsSettingsPage = lazy(
  () => import('@/pages/settings/ui/NotificationsSettingsPage'),
);
const PrivacySettingsPage = lazy(() => import('@/pages/settings/ui/PrivacySettingsPage'));
const ApiKeysSettingsPage = lazy(() => import('@/pages/settings/ui/ApiKeysSettingsPage'));
const ReferralPage = lazy(() => import('@/pages/referral/ui/ReferralPage'));
const AdminRolesPage = lazy(() => import('@/pages/admin/ui/AdminRolesPage'));
const AdminUsersPage = lazy(() => import('@/pages/admin/ui/AdminUsersPage'));
const AdminTelegramPage = lazy(() => import('@/pages/admin/ui/AdminTelegramPage'));
const AdminServicesPage = lazy(() => import('@/pages/admin/ui/AdminServicesPage'));
const AdminTariffsPage = lazy(() => import('@/pages/admin/ui/AdminTariffsPage'));
const LoginPage = lazy(() => import('@/pages/auth/ui/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/ui/RegisterPage'));
const OAuthCallbackPage = lazy(() => import('@/pages/auth/ui/OAuthCallbackPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ui/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ui/ResetPasswordPage'));
const VerifyEmailPage = lazy(() => import('@/pages/auth/ui/VerifyEmailPage'));
const NotFoundPage = lazy(() => import('@/pages/not-found/ui/NotFoundPage'));
const ForbiddenPage = lazy(() => import('@/pages/not-found/ui/ForbiddenPage'));
const TenderSearchPage = lazy(() => import('@/pages/tenders/search/TenderSearchPage'));
const TenderSearchResultsPage = lazy(() => import('@/pages/tenders/search/TenderSearchResultsPage'));
const TenderSchedulesPage = lazy(() => import('@/pages/tenders/schedules/TenderSchedulesPage'));
const TenderReportsPage = lazy(() => import('@/pages/tenders/reports/TenderReportsPage'));
const TenderBoardPage = lazy(() => import('@/pages/tenders/board/TenderBoardPage'));
const TenderTariffPage = lazy(() => import('@/pages/tenders/tariff/TenderTariffPage'));
const TenderOrdersPage = lazy(() => import('@/pages/tenders/orders/TenderOrdersPage'));
const TenderOnboardingPage = lazy(() => import('@/pages/tenders/onboarding/TenderOnboardingPage'));
const MatchingSetupWizardPage = lazy(() => import('@/pages/tenders/setup/MatchingSetupWizardPage'));
const TenderWorkspacePage = lazy(() => import('@/pages/tenders/workspace/TenderWorkspacePage'));
const TenderTemplatesPage = lazy(() => import('@/pages/tenders/templates/TenderTemplatesPage'));
const TenderCheckoutPage = lazy(() => import('@/pages/tenders/billing/TenderCheckoutPage'));

export function AppRouter() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<OAuthCallbackPage />} />

          <Route element={<AuthGuard />}>
            <Route element={<EmailVerificationGuard />}>
              <Route path="/app/verify-email" element={<VerifyEmailPage />} />
              <Route path="/app/verify-required" element={<VerifyEmailPage />} />
              <Route path="/app" element={<AppLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />

                <Route path="profile" element={<ProfilePage />} />
                <Route path="profile/edit" element={<ProfileEditPage />} />
                <Route path="profile/team" element={<CompanyTeamPage />} />

                <Route path="services/*" element={<Navigate to="/app/dashboard" replace />} />

                {/* Hidden for now — restore Projects* pages when ready */}
                <Route path="projects/*" element={<Navigate to="/app/dashboard" replace />} />

                <Route path="organizations" element={<OrganizationsPage />} />
                <Route path="organizations/invite" element={<OrganizationInvitePage />} />
                <Route path="organizations/:id" element={<OrganizationDetailPage />} />

                <Route path="activity" element={<StatisticsPage />} />
                <Route path="activity/history" element={<Navigate to="/app/cabinet/activity/history" replace />} />
                <Route path="cabinet/activity" element={<ActivityPage />} />
                <Route path="cabinet/activity/history" element={<ActivityHistoryPage />} />
                {/* Hidden for now — restore ArchivePage when ready */}
                <Route path="archive" element={<Navigate to="/app/cabinet/activity" replace />} />

                <Route path="billing" element={<Navigate to="/app/tenders/tariff" replace />} />
                <Route path="billing/plans" element={<Navigate to="/app/tenders/tariff" replace />} />
                <Route path="billing/payments" element={<Navigate to="/app/tenders/orders" replace />} />
                <Route path="billing/methods" element={<Navigate to="/app/tenders/tariff" replace />} />

                <Route path="settings" element={<Navigate to="/app/settings/interface" replace />} />
                <Route path="settings/interface" element={<InterfaceSettingsPage />} />
                <Route path="settings/security" element={<SecuritySettingsPage />} />
                <Route path="settings/notifications" element={<NotificationsSettingsPage />} />
                <Route path="settings/privacy" element={<PrivacySettingsPage />} />
                <Route path="settings/api-keys" element={<ApiKeysSettingsPage />} />

                <Route path="referral" element={<ReferralPage />} />

                <Route element={<TenderCabinetGuard />}>
                  <Route path="tenders/onboarding" element={<TenderOnboardingPage />} />
                  <Route path="tenders/setup" element={<MatchingSetupWizardPage />} />
                  <Route path="tenders" element={<Navigate to="/app/dashboard" replace />} />
                  <Route path="tenders/search" element={<TenderSearchPage />} />
                  <Route path="tenders/search/:id" element={<TenderSearchResultsPage />} />
                  <Route path="tenders/workspace/:tk" element={<TenderWorkspacePage />} />
                  <Route path="tenders/schedules" element={<TenderSchedulesPage />} />
                  {/* Hidden for now — restore when ready */}
                  <Route
                    path="tenders/saved-searches"
                    element={<Navigate to="/app/tenders/search" replace />}
                  />
                  <Route path="tenders/reports" element={<TenderReportsPage />} />
                  <Route path="tenders/board" element={<TenderBoardPage />} />
                  <Route path="tenders/templates" element={<TenderTemplatesPage />} />
                  <Route path="tenders/settings" element={<Navigate to="/app/profile/edit" replace />} />
                  <Route path="tenders/agent" element={<Navigate to="/app/tenders/board" replace />} />
                  <Route path="tenders/tariff" element={<TenderTariffPage />} />
                  <Route path="tenders/shop" element={<Navigate to="/app/tenders/tariff" replace />} />
                  <Route path="tenders/billing/checkout" element={<TenderCheckoutPage />} />
                  <Route path="tenders/orders" element={<TenderOrdersPage />} />
                </Route>

                <Route path="admin/users" element={<AdminUsersPage />} />
                  <Route path="admin/telegram" element={<AdminTelegramPage />} />
                <Route path="admin/tariffs" element={<AdminTariffsPage />} />
                <Route path="admin/services" element={<AdminServicesPage />} />
                <Route path="admin/roles" element={<AdminRolesPage />} />
              </Route>
            </Route>
          </Route>

          <Route path="/403" element={<ForbiddenPage />} />
          <Route path="/" element={<Navigate to="/app" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <CookieConsentBanner />
    </BrowserRouter>
  );
}
